import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ErrorIcon } from '@/assets/icons/error';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { isOddsBetrBucksSelector } from '@/feature/betslip-sbk/utils/betslip-selectors';
import {
    BetIds,
    MAX_ODDS,
    adjustOdds,
    getComboOdds,
    isComboBetSelectionsValid,
} from '@/feature/betslip-sbk/utils/betslip-utils';
import { useFormatOdds } from '@/hooks/use-format-odds';

export enum AlertKey {
    MULTI_SELECTION,
    EXCEED_ODDS_MULTIPLIER,
}

export type Alert = {
    type: AlertKey;
    message: string | React.JSX.Element;
};

export const ExceedOddsMultiplierAlertMsg = () => {
    const { showInfoSheet } = useAlerts();
    const { t } = useTranslation(['common', 'betslip_sbk']);
    const { formatOdds } = useFormatOdds();
    const formattedMaxOdds = formatOdds(MAX_ODDS, 0);

    const handleOnPress = () => {
        showInfoSheet({
            icon: (
                <Box mt="s16">
                    <ErrorIcon />
                </Box>
            ),
            title: t('betslip_sbk:exceed_odds_multiplier_modal_title'),
            description: t('betslip_sbk:exceed_odds_multiplier_modal_body', { limit: formattedMaxOdds }),
            buttonLabel: t('close'),
        });
    };

    return (
        <Trans
            i18nKey={'exceed_odds_multiplier_alert'}
            ns="betslip_sbk"
            values={{ limit: formattedMaxOdds }}
            components={{
                more: (
                    <Text
                        variant="titleSmall"
                        onPress={() => {
                            handleOnPress();
                        }}
                    />
                ),
            }}
        />
    );
};

export const useHasMaxOddsError = () => {
    return useSbkBetSlipStore(state => {
        const comboOdds = getComboOdds(state);
        const isBetrBucks = isOddsBetrBucksSelector(BetIds.COMBO)(state);
        return adjustOdds(comboOdds, isBetrBucks) > MAX_ODDS;
    });
};

export function useParlayCardAlert(): Alert | undefined {
    const { t } = useTranslation('betslip_sbk');
    const hasMultiSelectionAlert = useSbkBetSlipStore(state => !isComboBetSelectionsValid(state));
    const hasMaxOddsMultiplierAlert = useHasMaxOddsError();

    if (hasMultiSelectionAlert) {
        return {
            type: AlertKey.MULTI_SELECTION,
            message: t('multi_selection_parlay_alert'),
        };
    }

    if (hasMaxOddsMultiplierAlert) {
        return {
            type: AlertKey.EXCEED_ODDS_MULTIPLIER,
            message: <ExceedOddsMultiplierAlertMsg />,
        };
    }
}
